'use client';

import { useEffect, useRef } from 'react';
import {
  ArrowRight,
  Monitor,
  Bot,
  Wrench,
  Code,
  Workflow,
  Palette,
} from 'lucide-react';

/* ==================================================================
   Capability cards — AI-first framing. Rendered as a static 2 x 3
   grid (no scroll animation).
   ================================================================== */

const features = [
  {
    title: 'AI-First Cloud',
    desc: 'Scalable, secure cloud architecture built AI-ready from day one, tuned for real AI workloads.',
    Icon: Monitor,
    accent: 'Cloud',
  },
  {
    title: 'AI Development',
    desc: 'Production AI built into your product: agents, RAG, and intelligent automation that ships.',
    Icon: Bot,
    accent: 'AI',
  },
  {
    title: 'AI-First DevOps',
    desc: 'Automated CI/CD and infrastructure, accelerated by AI agents for faster, safer releases.',
    Icon: Wrench,
    accent: 'DevOps',
  },
  {
    title: 'AI-First Web Apps',
    desc: 'High-performance web apps engineered with AI woven through the entire stack.',
    Icon: Code,
    accent: 'Web',
  },
  {
    title: 'AI Automation',
    desc: 'Agentic workflows and intelligent automation that take repetitive work off your team, end to end.',
    Icon: Workflow,
    accent: 'Automation',
  },
  {
    title: 'AI-First Design',
    desc: 'Intuitive UI/UX shaped with AI, from rapid prototypes to polished, engaging interfaces.',
    Icon: Palette,
    accent: 'Design',
  },
];

function ExploreButton() {
  return (
    <a
      href="/ai-first-engineering"
      className="group inline-flex items-center gap-2 rounded-lg bg-[#058BF0] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cloud/30 transition hover:bg-[#0477cc]"
    >
      Explore Services
      <ArrowRight
        className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
        strokeWidth={2.2}
      />
    </a>
  );
}

export default function AIExpertise() {
  const pillRef = useRef(null);
  const sectionRef = useRef(null);

  /* Reveal on scroll-in: left column clears first, then the cards stagger up
     (delays + blur live in globals.css under `.aiexp`). Replays on re-entry. */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      section.classList.add('in');
      return;
    }
    let raf = null;
    const check = () => {
      const r = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom <= 0 || r.top >= vh) section.classList.remove('in');
      else if (r.top < vh * 0.82) section.classList.add('in');
    };
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Pill is a mouse affordance; taps on touch screens should not show it
  const movePill = e => {
    if (e.pointerType !== 'mouse') return;
    const el = pillRef.current;
    if (el) {
      el.style.opacity = '1';
      el.style.transform = `translate(${e.clientX}px, ${e.clientY}px) translate(-50%, -150%)`;
    }
  };
  const showPill = e => {
    if (e.pointerType !== 'mouse') return;
    const el = pillRef.current;
    if (el) el.style.opacity = '1';
  };
  const hidePill = () => {
    const el = pillRef.current;
    if (el) el.style.opacity = '0';
  };

  return (
    <section ref={sectionRef} data-nav-theme="light" className="aiexp relative z-10 bg-[#f6f8fb] py-20 lg:py-28">
      {/* Cursor-follow pill shown while hovering a capability card */}
      <div
        ref={pillRef}
        aria-hidden
        className="pointer-events-none fixed left-0 top-0 z-[60] whitespace-nowrap rounded-full bg-[#0a1b3a] px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white opacity-0 shadow-lg shadow-black/25 transition-opacity duration-200 will-change-transform"
      >
        Explore service
      </div>
      <div className="mx-auto w-11/12 max-w-[1280px]">
        <div className="grid items-center gap-12 lg:grid-cols-[2fr_3fr] lg:gap-12">
          {/* LEFT — eyebrow + heading + body + CTAs */}
          <div className="aiexp-left">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#058BF0] sm:text-sm">
              Our Expertise
            </p>

            <h2 className="mt-5 font-poppins text-3xl font-bold leading-[1.2] sm:leading-[1.2] lg:leading-[1.2] tracking-tight text-[#0a1b3a] sm:text-4xl lg:text-5xl xl:text-[3.5rem]">
              Capabilities That Span Your Entire Stack
            </h2>

            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-600 lg:text-lg">
              From cloud and DevOps to AI, web, and design, we bring production-grade, AI-first expertise to every layer of what we build for you.
            </p>

            {/* On phones the CTA moves below the cards */}
            <div className="mt-10 hidden sm:block">
              <ExploreButton />
            </div>
          </div>

          {/* RIGHT — static 2 x 3 grid: icon left, heading + body stacked right
              (phones: compact 2-col cards, icon above title) */}
          <div>
          <div className="grid grid-cols-2 gap-3 sm:gap-5">
            {features.map(({ title, Icon }) => (
              <a
                key={title}
                href="/ai-first-engineering"
                onPointerEnter={showPill}
                onPointerMove={movePill}
                onPointerLeave={hidePill}
                className="aiexp-card flex cursor-none flex-col items-start gap-2.5 rounded-2xl border border-[#0a1b3a]/10 bg-white p-4 hover:border-[#058BF0]/40 hover:shadow-md sm:flex-row sm:items-center sm:gap-3 sm:p-6 lg:p-7"
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#058BF0]/10 text-[#058BF0] sm:h-11 sm:w-11 sm:rounded-xl">
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.1} />
                </span>
                <h3 className="font-poppins text-sm font-bold leading-snug text-[#0a1b3a] sm:text-lg lg:text-xl">
                  {title}
                </h3>
              </a>
            ))}
          </div>
          <div className="mt-8 sm:hidden">
            <ExploreButton />
          </div>
          </div>
        </div>
      </div>
    </section>
  );
}
